import { Link } from "react-router-dom";
import { Instagram, Mail, ArrowUpRight } from "lucide-react";
import { useSection, useSettings, resolveUrl } from "../lib/useCMS";

const NAV_LINKS = [
  { label: "Programs", to: "/programs" },
  { label: "About", to: "/about" },
  { label: "Philosophy", to: "/philosophy" },
  { label: "Coach Mike", to: "/coach" },
  { label: "The Book", to: "/book" },
  { label: "Contact", to: "/contact" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy" },
  { label: "Terms of Use", to: "/terms" },
  { label: "Cookie Policy", to: "/cookies" },
];

const FALLBACK = {
  tagline: "Developing defensemen who think the game, embrace responsibility, and perform under pressure. Founded and coached by Mike Fournier.",
  copyright_left: "Defensemen-specific development.",
  copyright_right: "Built by Coach Mike Fournier.",
};

export const Footer = () => {
  const f = useSection("footer", FALLBACK);
  const settings = useSettings({
    logo_url: "https://customer-assets.emergentagent.com/job_hockey-landing-v2/artifacts/7dc9f10s_Built%20different%20primary%20_PNG.avif",
    brand_name: "Built Different",
    brand_tagline: "Hockey Development",
    contact_email: "mike@builtdifferenthockey.com",
    instagram_url: "https://www.instagram.com/builtdifferenthockey/",
    square_booking_url: "https://book.squareup.com/classes/krpp68j9ccjtzm/location/N7TC3KA55X2D8/classes",
  });

  return (
    <footer id="contact" data-testid="site-footer" className="relative bg-[#0a0a0a] border-t border-white/10 overflow-x-hidden">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 pt-20 pb-10">
        <div className="grid grid-cols-12 gap-x-3 gap-y-8 md:gap-10">
          <div className="col-span-12 md:col-span-6 min-w-0">
            <Link to="/" className="flex items-center gap-3 group">
              <img src={resolveUrl(settings.logo_url)} alt={settings.brand_name} loading="lazy" decoding="async" className="h-14 w-14 object-contain" />
              <div>
                <div className="font-display text-2xl uppercase leading-none">{settings.brand_name}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold mt-1">{settings.brand_tagline}</div>
              </div>
            </Link>
            <p className="mt-6 text-zinc-400 max-w-md">{f.tagline}</p>
            <div className="mt-6 flex items-center gap-3">
              <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" data-testid="footer-instagram-link" className="h-11 w-11 grid place-items-center border border-white/15 hover:border-brand-light hover:text-brand-light transition-colors" aria-label="Instagram">
                <Instagram className="h-4 w-4" />
              </a>
              <a href={`mailto:${settings.contact_email}`} data-testid="footer-email-link" className="h-11 w-11 grid place-items-center border border-white/15 hover:border-brand-light hover:text-brand-light transition-colors" aria-label="Email">
                <Mail className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div className="col-span-6 md:col-span-3 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold mb-5">Explore</div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-zinc-300 hover:text-brand-light transition-colors text-sm font-semibold tracking-wide">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-12 sm:col-span-6 md:col-span-3 min-w-0">
            <div className="text-[10px] tracking-[0.3em] uppercase text-brand-light font-bold mb-5">Contact</div>
            <a href={`mailto:${settings.contact_email}`} data-testid="footer-contact-email" className="group flex items-start gap-2 text-zinc-200 hover:text-brand-light transition-colors min-w-0">
              <span className="font-display uppercase text-sm sm:text-base md:text-lg leading-tight min-w-0" style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}>
                {settings.contact_email}
              </span>
              <ArrowUpRight className="h-4 w-4 mt-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
            </a>
            <a href={settings.square_booking_url} target="_blank" rel="noopener noreferrer" data-testid="footer-book-link" className="mt-5 inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-brand-light transition-colors">
              Book a clinic on Square
              <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      <div className="px-6 md:px-10 lg:px-16 overflow-hidden">
        <div
          className="font-display uppercase text-white text-[clamp(3rem,17vw,20rem)] tracking-[-0.04em] select-none flex flex-wrap items-end gap-x-[0.18em] gap-y-1 pt-3 pb-1"
          style={{ lineHeight: 1.02 }}
          data-testid="footer-display-text"
        >
          <span>BUILT</span>
          <span className="text-outline-red">DIFFERENT</span>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-zinc-500">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span>© {new Date().getFullYear()} Built Different Hockey. All rights reserved.</span>
          </div>
          <nav className="flex flex-wrap items-center gap-x-5 gap-y-2" aria-label="Legal">
            {LEGAL_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`footer-legal-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-zinc-400 hover:text-brand-light transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <span>{f.copyright_left}</span>
            <span className="h-1 w-1 bg-brand-light rotate-45 hidden sm:inline-block" />
            <span className="hidden sm:inline">{f.copyright_right}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
